import { useCallback, useEffect, useRef, useState } from 'react';
import type { Game } from '../types/index.js';
import { useFitText } from '../hooks/useFitText.js';

// ============================================================
// TeamSquare — internal to Scoreboard
// ============================================================

export interface TeamSquareProps {
  teamId: 'team1' | 'team2';
  team: Game['team1'];
  score: number;
  color: string;
  textColor: string;
  isDoubles: boolean;
  /** Standard tap — opens score entry overlay (authenticated PPR/simple flow) */
  onTap?: (teamId: 'team1' | 'team2') => void;
  /**
   * Zone tap — upper half = +1, lower half = −1.
   * Used by anonymous simple mode: no overlay, direct increment/decrement.
   * Mutually exclusive with onTap — if both are provided, onZoneTap wins.
   */
  onZoneTap?: (teamId: 'team1' | 'team2', delta: 1 | -1) => void;
  /** Called when useFitText computes the ideal size for a name slot */
  onNameSize?: (which: 'a' | 'b', size: number) => void;
  /** When set, overrides all computed sizes — used for cross-team balancing */
  sharedFontSize?: number;
}

export function TeamSquare({ teamId, team, score, color, textColor, isDoubles, onTap, onZoneTap, onNameSize, sharedFontSize }: TeamSquareProps) {
  const playerA = team.players[0];
  const playerB = team.players[1];

  // Track the name zone dimensions so useFitText has the right pixel budget
  // and an appropriate start size.
  const nameZoneRef = useRef<HTMLDivElement>(null);
  const [maxNamePx, setMaxNamePx] = useState(0);
  const [nameZoneHeight, setNameZoneHeight] = useState(0);

  useEffect(() => {
    const el = nameZoneRef.current;
    if (!el) return;

    const measure = () => {
      setMaxNamePx(el.clientWidth);
      setNameZoneHeight(el.clientHeight);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Start size: fill the full name zone height so we shrink from maximum.
  // Falls back to 200px before the zone has been measured.
  const startFontSize = nameZoneHeight > 0 ? Math.floor(nameZoneHeight * 0.95) : 200;

  // Stable callbacks so useFitText deps don't change on every render
  const handleNameASize = useCallback(
    (size: number) => onNameSize?.('a', size),
    [onNameSize],
  );
  const handleNameBSize = useCallback(
    (size: number) => onNameSize?.('b', size),
    [onNameSize],
  );

  const nameARef = useFitText(
    playerA?.name ?? '',
    maxNamePx,
    startFontSize,
    handleNameASize,
    sharedFontSize,
  );
  const nameBRef = useFitText(
    playerB?.name ?? '',
    maxNamePx,
    startFontSize,
    handleNameBSize,
    sharedFontSize,
  );

  return (
    /*
     * container-type: size makes this column a CSS size-query container.
     * All cqh values below are relative to THIS element's height
     * (not the viewport, not the parent). Since this column fills the
     * full scoreboard height (flex: 1 within a height: 100% parent),
     * 1cqh here ≈ 1% of scoreboard height. Layout proportions:
     *
     *   Name A zone:  height 15cqh  →  15% of scoreboard height
     *   Score square: flex: 1       →  ~70% of scoreboard height
     *   Name B zone:  height 15cqh  →  15% of scoreboard height
     *
     * Score font = 75% of score area = 75% × 70cqh = 52.5cqh
     * Name font  = fit-text measured in px (starts at zone height, shrinks
     *              to fit 35vw budget; shared minimum applied across all names)
     */
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 0,
        minHeight: 0,
        containerType: 'size',
      }}
    >
      {/*
       * Player A name — top 15cqh zone.
       * overflow: visible on both container and span so descenders
       * (g, y, p, q, etc.) are never clipped by the zone boundary.
       * lineHeight: 1 — avoids extra half-leading that pushes text below visual center.
       * maxWidth: 35vw — constrains name to 40% of screen width.
       * useFitText hook starts at zone height and shrinks until text fits.
       */}
      <div
        ref={nameZoneRef}
        style={{
          height: '15cqh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '35vw',
          flexShrink: 0,
          overflow: 'visible',
        }}
      >
        <span
          ref={nameARef}
          style={{
            fontWeight: 700,
            textAlign: 'center',
            color: textColor,
            lineHeight: 1,
            overflow: 'visible',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {playerA?.name ?? ''}
        </span>
      </div>

      {/*
       * Score square — fills the middle ~70cqh.
       *
       * Two modes:
       *   onZoneTap (anonymous simple): split into top/bottom halves.
       *     Top half tap = +1, bottom half tap = −1. No overlay.
       *   onTap (authenticated): single tap target opens score entry overlay.
       *   Neither: display only (no cursor, no interaction).
       */}
      {onZoneTap ? (
        /* ── Zone-tap mode: single element, position-based delta ── */
        <div
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const delta = e.clientY < rect.top + rect.height / 2 ? 1 : -1;
            onZoneTap(teamId, delta as 1 | -1);
          }}
          role="button"
          aria-label={`${team.name} score: ${score}. Tap top to increase, bottom to decrease.`}
          style={{
            flex: 1,
            width: '100%',
            backgroundColor: color,
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px',
            cursor: 'pointer',
            touchAction: 'manipulation',
            position: 'relative',
            overflow: 'visible',
            userSelect: 'none',
          }}
        >
          <span
            style={{
              fontSize: '52.5cqh',
              fontWeight: 500,
              color: textColor,
              lineHeight: 1,
              overflow: 'visible',
              pointerEvents: 'none',
            }}
          >
            {score}
          </span>
        </div>
      ) : (
        /* ── Standard tap / display-only mode ── */
        <button
          onClick={onTap ? () => onTap(teamId) : undefined}
          aria-label={onTap ? `${team.name} score: ${score}. Tap to enter score.` : `${team.name} score: ${score}`}
          style={{
            flex: 1,
            width: '100%',
            backgroundColor: color,
            border: 'none',
            borderRadius: '12px',
            cursor: onTap ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px',
            touchAction: onTap ? 'manipulation' : 'none',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            padding: '8px',
            overflow: 'visible',
          }}
        >
          {/*
           * Score number — dominates the square, readable across the room.
           * 52.5cqh = 75% of the ~70cqh score area (jumbotron scale).
           * fontWeight 500 — bold enough without being ultra-heavy.
           * lineHeight: 1 keeps it compact; overflow: visible prevents clipping.
           */}
          <span
            style={{
              fontSize: '52.5cqh',
              fontWeight: 500,
              color: textColor,
              lineHeight: 1,
              overflow: 'visible',
            }}
          >
            {score}
          </span>
        </button>
      )}

      {/*
       * Player B name — bottom 15cqh zone.
       * ALWAYS present (even in singles) to keep layout consistent.
       * In singles: empty spacer — no name rendered.
       * In doubles: shows partner name with same fit-text treatment.
       * maxWidth: 35vw mirrors the Player A zone constraint.
       */}
      <div
        style={{
          height: '15cqh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '35vw',
          flexShrink: 0,
          overflow: 'visible',
        }}
      >
        {isDoubles && playerB && (
          <span
            ref={nameBRef}
            style={{
              fontWeight: 700,
              textAlign: 'center',
              color: textColor,
              lineHeight: 1,
              overflow: 'visible',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {playerB.name}
          </span>
        )}
      </div>
    </div>
  );
}
